/**
 * PurchaseImpact Component
 *
 * Visual representation of how a purchase will impact finances
 * across different time horizons (immediate, weekly, monthly).
 * Uses animated progress bars and color-coded feedback.
 */

import { Box, Typography, LinearProgress, keyframes } from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

export interface PurchaseImpactData {
  immediate: number;
  nextWeek: number;
  nextMonth: number;
}

export interface PurchaseImpactProps {
  impact: PurchaseImpactData;
  currentBalance: number;
  purchaseAmount: number;
  compact?: boolean;
}

// Slide in animation
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Helper function to determine impact severity
function getImpactSeverity(
  impactValue: number,
  balance: number
): 'positive' | 'neutral' | 'negative' | 'critical' {
  const percentOfBalance = (impactValue / balance) * 100;

  if (impactValue >= 0) return 'positive';
  if (percentOfBalance > -10) return 'neutral';
  if (percentOfBalance > -30) return 'negative';
  return 'critical';
}

// Color mapping for severity
const severityColors = {
  positive: { main: '#10B981', bg: 'rgba(16, 185, 129, 0.15)', text: '#059669' },
  neutral: { main: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)', text: '#D97706' },
  negative: { main: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)', text: '#DC2626' },
  critical: { main: '#DC2626', bg: 'rgba(220, 38, 38, 0.2)', text: '#B91C1C' },
};

// Icon for each severity
function SeverityIcon({ severity }: { severity: keyof typeof severityColors }) {
  const iconProps = {
    sx: { fontSize: 20, color: severityColors[severity].main },
  };

  switch (severity) {
    case 'positive':
      return <TrendingUpIcon {...iconProps} />;
    case 'neutral':
      return <TrendingFlatIcon {...iconProps} />;
    case 'negative':
    case 'critical':
      return <TrendingDownIcon {...iconProps} />;
    default:
      return <TrendingFlatIcon {...iconProps} />;
  }
}

interface ImpactRowProps {
  label: string;
  value: number;
  balance: number;
  delay: number;
  compact?: boolean;
}

function ImpactRow({ label, value, balance, delay, compact }: ImpactRowProps) {
  const severity = getImpactSeverity(value, balance);
  const colors = severityColors[severity];
  const percentImpact = Math.min(100, Math.abs((value / balance) * 100));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        animation: `${slideIn} 0.4s ease-out`,
        animationDelay: `${delay}ms`,
        animationFillMode: 'backwards',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SeverityIcon severity={severity} />
          <Typography
            variant={compact ? 'caption' : 'body2'}
            sx={{ color: 'text.secondary', fontWeight: 500 }}
          >
            {label}
          </Typography>
        </Box>
        <Typography
          variant={compact ? 'body2' : 'body1'}
          sx={{
            fontWeight: 700,
            color: colors.text,
            fontFamily: 'monospace',
          }}
        >
          {value >= 0 ? '+' : ''}${value.toFixed(2)}
        </Typography>
      </Box>

      {!compact && (
        <Box sx={{ position: 'relative' }}>
          <LinearProgress
            variant="determinate"
            value={percentImpact}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: colors.main,
                borderRadius: 4,
                transition: 'width 0.5s ease-out',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export function PurchaseImpact({
  impact,
  currentBalance,
  purchaseAmount,
  compact = false,
}: PurchaseImpactProps) {
  // Calculate what balance will be after purchase
  const afterPurchaseBalance = currentBalance - purchaseAmount;

  // Determine overall purchase health
  const overallSeverity = getImpactSeverity(impact.nextMonth, currentBalance);
  const isHealthy = overallSeverity === 'positive' || overallSeverity === 'neutral';

  return (
    <Box
      sx={{
        backgroundColor: compact ? 'transparent' : 'background.paper',
        borderRadius: compact ? 0 : 3,
        p: compact ? 0 : 3,
        border: compact ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      {!compact && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <span style={{ fontSize: '1.25rem' }}>📊</span>
          Purchase Impact
        </Typography>
      )}

      {/* Balance Summary - Only in non-compact mode */}
      {!compact && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: isHealthy
              ? 'rgba(16, 185, 129, 0.1)'
              : 'rgba(239, 68, 68, 0.1)',
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Current Balance
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
              ${currentBalance.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="h4">→</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              After Purchase
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontFamily: 'monospace',
                color: afterPurchaseBalance >= 0 ? 'success.main' : 'error.main',
              }}
            >
              ${afterPurchaseBalance.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Impact Timeline */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? 1.5 : 2.5,
        }}
      >
        <ImpactRow
          label="Immediate"
          value={impact.immediate}
          balance={currentBalance}
          delay={0}
          compact={compact}
        />
        <ImpactRow
          label="Next Week"
          value={impact.nextWeek}
          balance={currentBalance}
          delay={100}
          compact={compact}
        />
        <ImpactRow
          label="Next Month"
          value={impact.nextMonth}
          balance={currentBalance}
          delay={200}
          compact={compact}
        />
      </Box>

      {/* Warning message for critical impacts */}
      {!compact && overallSeverity === 'critical' && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(220, 38, 38, 0.1)',
            border: '1px solid',
            borderColor: 'error.main',
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: 'error.main', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <span>⚠️</span>
            This purchase may significantly impact your financial stability
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default PurchaseImpact;
